/**
 * Phone Filters Component
 *
 * Provides search, filter, and sort controls for the phone list.
 */

import { useState, useCallback } from 'react';
import { cn, debounce } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { SearchInput, Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import {
  usePhoneStore,
  usePhoneFilters,
  useCallGroups,
  useFilteredPhonesCount,
} from '@/stores/phoneStore';
import { Filter, X, RotateCcw } from 'lucide-react';

// ============================================================================
// Component
// ============================================================================

export function PhoneFilters() {
  const filters = usePhoneFilters();
  const callGroups = useCallGroups();
  const filteredCount = useFilteredPhonesCount();

  const { setFilters, resetFilters } = usePhoneStore();

  const [localSearch, setLocalSearch] = useState(filters.search);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Debounced search
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      Logger.debug(LogCategory.DATA, 'Search filter applied', 'PhoneFilters', { search: value });
      setFilters({ search: value });
    }, 300),
    [setFilters]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedSetSearch(value);
  };

  const handleClearSearch = () => {
    Logger.debug(LogCategory.UI, 'Search cleared', 'PhoneFilters');
    setLocalSearch('');
    setFilters({ search: '' });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as 'all' | 'clean' | 'flagged';
    Logger.debug(LogCategory.DATA, 'Status filter changed', 'PhoneFilters', { status: value });
    setFilters({ status: value });
  };

  const handlePhoneNumberGroupChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    Logger.debug(LogCategory.DATA, 'Phone number group filter changed', 'PhoneFilters', {
      phoneNumberGroup: e.target.value,
    });
    setFilters({ phoneNumberGroup: e.target.value });
  };

  const handleDateRangeChange = (field: 'start' | 'end') => (e: React.ChangeEvent<HTMLInputElement>) => {
    Logger.debug(LogCategory.DATA, 'Date range filter changed', 'PhoneFilters', {
      field,
      value: e.target.value,
    });
    setFilters({
      dateRange: {
        ...filters.dateRange,
        [field]: e.target.value || null,
      },
    });
  };

  const handleReset = () => {
    Logger.info(LogCategory.UI, 'Filters reset', 'PhoneFilters');
    setLocalSearch('');
    resetFilters();
  };

  const hasActiveFilters =
    filters.search ||
    filters.status !== 'all' ||
    filters.phoneNumberGroup !== 'all' ||
    filters.dateRange.start ||
    filters.dateRange.end;

  // Build phone number group options
  const phoneNumberGroupOptions = [
    { value: 'all', label: 'All Groups' },
    { value: 'unassigned', label: 'Unassigned' },
    ...callGroups.map((group) => ({
      value: group.name,
      label: group.name,
    })),
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
      {/* Main filters row */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="flex-1 min-w-[200px] max-w-md">
          <SearchInput
            placeholder="Search phone numbers..."
            value={localSearch}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
          />
        </div>

        {/* Status filter */}
        <Select
          value={filters.status}
          onChange={handleStatusChange}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'clean', label: 'Clean' },
            { value: 'flagged', label: 'Flagged' },
          ]}
          size="md"
          fullWidth={false}
          className="w-36"
        />

        {/* Phone number group filter */}
        <Select
          value={filters.phoneNumberGroup}
          onChange={handlePhoneNumberGroupChange}
          options={phoneNumberGroupOptions}
          size="md"
          fullWidth={false}
          className="w-48"
        />

        {/* Advanced toggle */}
        <Button
          variant={showAdvanced ? 'secondary' : 'ghost'}
          size="md"
          leftIcon={<Filter className="h-4 w-4" />}
          onClick={() => {
            Logger.debug(LogCategory.UI, 'Advanced filters toggled', 'PhoneFilters');
            setShowAdvanced(!showAdvanced);
          }}
        >
          Advanced
        </Button>

        {/* Reset filters */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="md"
            leftIcon={<RotateCcw className="h-4 w-4" />}
            onClick={handleReset}
          >
            Reset
          </Button>
        )}
      </div>

      {/* Advanced filters */}
      {showAdvanced && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-48">
              <Input
                label="Last Flagged From"
                type="date"
                value={filters.dateRange.start || ''}
                onChange={handleDateRangeChange('start')}
                size="sm"
              />
            </div>
            <div className="w-48">
              <Input
                label="Last Flagged To"
                type="date"
                value={filters.dateRange.end || ''}
                onChange={handleDateRangeChange('end')}
                size="sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Results count */}
      <div className="mt-3 text-sm text-slate-500">
        {filteredCount.toLocaleString()} phone number{filteredCount !== 1 ? 's' : ''} found
      </div>
    </div>
  );
}
